import { useState } from "react";
import { Button, TextField, Box, Snackbar, Alert } from "@mui/material";
import PageMeta from "../components/common/PageMeta";
import PageContainer from "../components/common/PageContainer";
import PageHeader from "../components/common/PageHeader";
import { useApi } from "../hooks/useApi";

export default function VerifiPortalImport() {
  const { post } = useApi();
  const [cookies, setCookies] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error" | "warning";
  }>({ open: false, message: "", severity: "success" });

  const handleImport = async () => {
    if (!cookies.trim()) return;
    setSubmitting(true);
    try {
      const result = await post<{ jobId: string; message: string }>(
        "/api/admin/verifi-portal/import",
        { cookies: cookies.trim() },
      );
      setSnackbar({
        open: true,
        message: result!.message,
        severity: "success",
      });
      setCookies("");
    } catch (err: any) {
      const severity: "warning" | "error" = err.status === 409 ? "warning" : "error";
      setSnackbar({ open: true, message: err.message || "Import failed", severity });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <PageMeta title="Chargemont | Verifi Import" description="Import clients and merchants from Verifi Portal" />
      <PageContainer>
        <PageHeader title="Verifi Import" />

        <p className="mb-6 text-sm text-gray-600 max-w-3xl">
          This page imports client and merchant records from the Verifi Portal into the Chargemont Portal.
        </p>

        <p className="mb-6 text-sm text-gray-600 max-w-3xl">
          Single import picks up everything created since the last one.
          Run one import to bring them all in — after a batch of changes, or at the end of the day.
        </p>

        {/* Instructions */}
        <Box className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
          <h4 className="text-sm font-semibold text-blue-800 mb-2">How to export cookies</h4>
          <ol className="text-sm text-blue-700 list-decimal ml-4 space-y-2">
            <li>Install the <strong>Cookie-Editor</strong> browser extension (<a href="https://chromewebstore.google.com/detail/cookie-editor/hlkenndednhfkekhgcdicdfddnkalmdm" target="_blank" rel="noopener noreferrer" className="underline">Chrome</a> / <a href="https://addons.mozilla.org/en-US/firefox/addon/cookie-editor/" target="_blank" rel="noopener noreferrer" className="underline">Firefox</a>)</li>
            <li>Log into <strong>verifi-one.visa.com</strong></li>
            <li>Click the <strong>Cookie-Editor</strong> icon in the toolbar</li>
            <li>
              Click <strong>Export</strong> → <strong>Header String</strong>
              <img
                src="/images/verifi-portal/cookie-editor-steps.png"
                alt="Open Cookie-Editor, click Export, then choose Header String"
                className="mt-2 rounded-lg border border-blue-200 max-w-full w-[640px]"
              />
            </li>
            <li>Paste the result below and click <strong>Import</strong></li>
          </ol>
          <p className="text-sm text-blue-700 mt-3">
            The import runs in the background. Results (new clients, merchants, backfilled notices) are posted to <strong>#dashboard-notifications</strong> on Slack when it completes.
          </p>
        </Box>

        {/* Form */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, maxWidth: 700 }}>
          <TextField
            label="Cookies"
            placeholder="JSESSIONID=abc123; SID=xyz789; CSRF_COOKIE=...; c_token=...; MFAID=..."
            value={cookies}
            onChange={(e) => setCookies(e.target.value)}
            disabled={submitting}
            multiline
            rows={3}
            size="small"
          />
          <Box>
            <Button
              onClick={handleImport}
              variant="contained"
              disabled={submitting || !cookies.trim()}
              sx={{ textTransform: "none" }}
            >
              {submitting ? "Starting..." : "Start Import"}
            </Button>
          </Box>
        </Box>
      </PageContainer>

      <Snackbar
        open={snackbar.open}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
}
